import { Button, Grid, IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Actions from 'actions'
import { API } from 'aws-amplify'
import PrimaryButton from 'components/common/buttons/PrimaryButton'
import SecondaryButton from 'components/common/buttons/SecondaryButton'
import ConfirmationModal from 'components/common/ConfirmationModal'
import { Copy, TrashIcon } from 'components/common/icons'
import BaseModal from 'components/common/modals/BaseModal'
import PreviouslyUploadedStls from 'components/Patients/PatientCard/TxTrackerSetup/PreviouslyUploadedStls'
import moment from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import { parseTxTrackerData } from 'components/Patients/TreatmentTracker/trackingStampUtils'
import { RetentionReferenceStatuses } from 'components/Patients/TreatmentTracker/txTrackerConsts'
import { ScanSummaryStatus } from 'consts/scanSummaryConsts'
import { buildRetentionReferenceRows, buildRetentionScanRows, buildActiveScanRows } from './txTrackerAdminUtils'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'
import { logError, logInfo } from 'utils/logUtils'

const useStyles = makeStyles({
  root: {
    height: '100%',
    width: '70vw',
    maxWidth: '100%',
    padding: 32,
    alignItems: 'space-between'
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  buttonsContainer: {
    display: 'flex'
  }
})

// Completed (scan runs) and ready (references) are the good end states of their
// respective lifecycles; failed is terminal for both; anything else is in flight or absent.
const GOOD_END_STATUSES = [ScanSummaryStatus.Completed, RetentionReferenceStatuses.Ready]

const statusColor = status =>
  GOOD_END_STATUSES.includes(status) ? 'green' : status === ScanSummaryStatus.Failed ? 'red' : 'gray'

const TxTrackerAdminModal = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [confirmationModalType, setConfirmationModalType] = useState(null)

  const isOpen = useSelector(state => state.txTrackerReducer.txTrackerAdminModal.isOpen)
  const patient = useSelector(state => state.patientsReducer.patient)
  const grinScans = useSelector(state => state.treatmentReducer.grinScans)
  const treatment = useSelector(state => state.treatmentReducer.treatment)
  const { type: treatmentType, txTrackerStls } = treatment || {}

  const patientAppSettings = useMemo(() => JSON.parse(patient?.user?.appSettings || '{}'), [patient])
  const patientDoctorAppSettings = useMemo(() => JSON.parse(patient?.doctor?.user?.appSettings || '{}'), [patient])
  const parsedTxTrackerStls = useMemo(() => JSON.parse(txTrackerStls || '{}'), [txTrackerStls])

  const [isLoading, setIsLoading] = useState(false)
  const [isEditingFreezeVersion, setIsEditingFreezeVersion] = useState(false)
  const [isBulk, setIsBulk] = useState(false)
  const [isRetentionView, setIsRetentionView] = useState(false)
  const [patientIds, setPatientIds] = useState('')

  const retentionReferenceRows = useMemo(
    () => buildRetentionReferenceRows(treatment?.txTrackerRetentionStls),
    [treatment]
  )
  const retentionScanRows = useMemo(() => buildRetentionScanRows(grinScans), [grinScans])
  const activeScanRows = useMemo(() => buildActiveScanRows(grinScans), [grinScans])

  const parsedPatientIds = useMemo(
    () =>
      patientIds
        ? patientIds
            .replace(/\s/g, '')
            .replace(/\n/g, '')
            .split(',')
            .filter(p => !!p)
        : null,
    [patientIds]
  )

  const handleClose = useCallback(() => {
    dispatch(Actions.toggleTxTrackerModalOpen(false))
  }, [dispatch])

  const rerunAndDelete = useCallback(() => {
    setIsLoading(true)
    Promise.allSettled(
      (parsedPatientIds || [patient.id]).map(patientId => {
        trackEvent('Tx Tracker Admin - rerun and delete all clicked', {
          patientId: patient.id,
          partOfBulk: !!parsedPatientIds
        })
        return API.post('grinServerlessApi', '/treatment-tracker/v1/ops/rerunAndDelete', { body: { patientId } })
      })
    )
      .then(results => {
        const fulfilled = results.filter(result => result.status === 'fulfilled')
        const rejected = results.filter(result => result.status === 'rejected')

        setIsLoading(false)
        dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: false }))

        if (fulfilled.length > 0) {
          logInfo('Rerun & Delete succeeded for patients', {
            patientIds: fulfilled.map(result => result.value?.patientId),
            responses: fulfilled.map(result => result.value)
          })
        }

        if (rejected.length > 0) {
          logError('Rerun & Delete failed for some patients', {
            errors: rejected.map(result => result.reason)
          })
          console.log('rejected', JSON.stringify(rejected))
          dispatch(
            Actions.showSnackbar({
              type: 'error',
              text: 'Rerun & Delete failed for some patients. open console to view'
            })
          )
        }
      })
      .catch(ex => {
        logError('Unexpected error during rerun & delete', { error: ex })
        setIsLoading(false)
      })
  }, [patient, dispatch, parsedPatientIds])

  const initTxTracker = useCallback(() => {
    setIsLoading(true)

    Promise.allSettled(
      (parsedPatientIds || [patient.id]).map(patientId => {
        trackEvent('Tx Tracker Admin - init/rerun clicked', { patientId: patient.id, partOfBulk: !!parsedPatientIds })
        return API.post('grinServerlessApi', '/treatment-tracker/v1/ops/init', { body: { patientId } })
      })
    )
      .then(results => {
        const fulfilled = results.filter(result => result.status === 'fulfilled')
        const rejected = results.filter(result => result.status === 'rejected')

        setIsLoading(false)
        dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: false }))

        if (fulfilled.length > 0) {
          logInfo('init/rerun succeeded for patients', {
            patientIds: fulfilled.map(result => result.value?.patientId),
            responses: fulfilled.map(result => result.value)
          })
        }

        if (rejected.length > 0) {
          logError('init/rerun failed for some patients', {
            errors: rejected.map(result => result.reason)
          })
          console.log('rejected', JSON.stringify(rejected))
          dispatch(
            Actions.showSnackbar({
              type: 'error',
              text: 'init/rerun failed for some patients. open console to view'
            })
          )
        }
      })
      .catch(ex => {
        logError('Unexpected error during init/rerun', { error: ex })
        setIsLoading(false)
      })
  }, [patient, dispatch, parsedPatientIds])

  const deleteAll = useCallback(() => {
    setIsLoading(true)
    Promise.allSettled(
      (parsedPatientIds || [patient.id]).map(patientId => {
        trackEvent('Tx Tracker Admin - delete all clicked', { patientId: patient.id, partOfBulk: !!parsedPatientIds })
        return API.post('grinServerlessApi', '/treatment-tracker/v1/ops/deleteAll', { body: { patientId } })
      })
    )
      .then(results => {
        const fulfilled = results.filter(result => result.status === 'fulfilled')
        const rejected = results.filter(result => result.status === 'rejected')

        setIsLoading(false)
        dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: false }))

        if (fulfilled.length > 0) {
          logInfo('delete all succeeded for patients', {
            patientIds: fulfilled.map(result => result.value?.patientId),
            responses: fulfilled.map(result => result.value)
          })
        }

        if (rejected.length > 0) {
          logError('delete all failed for some patients', {
            errors: rejected.map(result => result.reason)
          })
          console.log('rejected', JSON.stringify(rejected))
          dispatch(
            Actions.showSnackbar({
              type: 'error',
              text: 'delete all failed for some patients. open console to view'
            })
          )
        }
      })
      .catch(ex => {
        logError('Unexpected error during delete all', { error: ex })
        setIsLoading(false)
      })
  }, [patient, dispatch, parsedPatientIds])

  const setPatientFreezeVersion = useCallback(
    version => {
      if (!version) {
        alert("You didn't provide any new freeze version")
        return
      }

      setIsLoading(true)
      Promise.allSettled(
        (parsedPatientIds || [patient.id]).map(patientId => {
          trackEvent('Tx Tracker Admin - set patient freeze version', {
            patientId: patient.id,
            partOfBulk: !!parsedPatientIds
          })
          return API.post('grinServerlessApi', '/treatment-tracker/v1/ops/setTxVersion', {
            body: { patientId, version }
          })
        })
      )
        .then(results => {
          const fulfilled = results.filter(result => result.status === 'fulfilled')
          const rejected = results.filter(result => result.status === 'rejected')

          setIsLoading(false)
          setConfirmationModalType(null)
          dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: false }))

          if (fulfilled.length > 0) {
            logInfo('set patient freeze version succeeded for patients', {
              patientIds: fulfilled.map(result => result.value?.patientId),
              responses: fulfilled.map(result => result.value)
            })
          }

          if (rejected.length > 0) {
            logError('set patient freeze version failed for some patients', {
              errors: rejected.map(result => result.reason)
            })
            console.log('rejected', JSON.stringify(rejected))
            dispatch(
              Actions.showSnackbar({
                type: 'error',
                text: 'set patient freeze version failed for some patients. open console to view'
              })
            )
          }
        })
        .catch(ex => {
          logError('Unexpected error during set patient freeze version', { error: ex })
          setIsLoading(false)
        })
    },
    [patient, dispatch, parsedPatientIds]
  )

  const deleteScanResults = useCallback(
    scanId => {
      setIsLoading(true)
      trackEvent('Tx Tracker Admin - delete scan tx results', { patientId: patient.id })

      API.post('grinServerlessApi', '/treatment-tracker/v1/ops/deleteScanResults', {
        body: {
          scanId
        }
      })
        .then(r => {
          setIsLoading(false)
          setConfirmationModalType(null)
          logInfo('Delete Scan Results succeeded', { patientId: patient.id, response: r })
          // Nothing pushes the new scan status into the store, and the documented rerun
          // workflow is delete-then-rerun, so the operator needs the row to change.
          dispatch(Actions.requestGrinScans(patient.id))
          dispatch(Actions.showSnackbar({ type: 'success', text: 'Scan tx results deleted' }))
        })
        .catch(ex => {
          setIsLoading(false)
          setConfirmationModalType(null)
          logError('Delete Scan Results failed', { patientId: patient.id, error: ex })
          dispatch(
            Actions.showSnackbar({
              type: 'error',
              text: `Delete scan results failed: ${ex?.response?.data?.message || ex?.message}`
            })
          )
        })
    },
    [patient, dispatch]
  )

  const deleteStls = useCallback(() => {
    setIsLoading(true)
    trackEvent('Tx Tracker Admin - delete treatment stls', { patientId: patient.id })

    API.post('grinServerlessApi', '/treatment-tracker/v1/ops/deleteTreatmentStls', {
      body: {
        patientId: patient.id
      }
    })
      .then(r => {
        setIsLoading(false)
        setConfirmationModalType(null)
        logInfo('Delete Treatment Stls succeeded', { patientId: patient.id, response: r })
      })
      .catch(ex => {
        setIsLoading(false)
        setConfirmationModalType(null)
        logError('Delete Treatment Stls failed', { patientId: patient.id, error: ex })
      })
  }, [patient])

  const deleteAllAlignersInfo = useCallback(() => {
    setIsLoading(true)
    Promise.allSettled(
      (parsedPatientIds || [patient.id]).map(patientId => {
        trackEvent('Tx Tracker Admin - delete all aligners info', {
          patientId: patient.id,
          partOfBulk: !!parsedPatientIds
        })
        return API.post('grinServerlessApi', '/treatment-tracker/v1/ops/deleteAlignersInfo', { body: { patientId } })
      })
    )
      .then(results => {
        const fulfilled = results.filter(result => result.status === 'fulfilled')
        const rejected = results.filter(result => result.status === 'rejected')

        setIsLoading(false)
        dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: false }))

        if (fulfilled.length > 0) {
          logInfo('delete all aligners info succeeded for patients', {
            patientIds: fulfilled.map(result => result.value?.patientId),
            responses: fulfilled.map(result => result.value)
          })
        }

        if (rejected.length > 0) {
          logError('delete all aligners info failed for some patients', {
            errors: rejected.map(result => result.reason)
          })
          console.log('rejected', JSON.stringify(rejected))
          dispatch(
            Actions.showSnackbar({
              type: 'error',
              text: 'delete all aligners info failed for some patients. open console to view'
            })
          )
        }
      })
      .catch(ex => {
        logError('Unexpected error during delete all aligners info', { error: ex })
        setIsLoading(false)
      })
  }, [patient, dispatch, parsedPatientIds])

  const rerunRetentionScan = useCallback(
    scanId => {
      setIsLoading(true)
      trackEvent('Tx Tracker Admin - rerun retention scan', { patientId: patient.id, scanId })

      API.post('grinServerlessApi', '/treatment-tracker/v1/ops/rerunRetentionScan', {
        body: {
          scanId
        }
      })
        .then(r => {
          setIsLoading(false)
          setConfirmationModalType(null)
          logInfo('Rerun Retention Scan succeeded', { patientId: patient.id, scanId, response: r })
          dispatch(Actions.requestGrinScans(patient.id))
          dispatch(Actions.showSnackbar({ type: 'success', text: 'Retention model dispatched' }))
        })
        .catch(ex => {
          setIsLoading(false)
          setConfirmationModalType(null)
          logError('Rerun Retention Scan failed', { patientId: patient.id, scanId, error: ex })
          dispatch(
            Actions.showSnackbar({
              type: 'error',
              text: `Rerun failed: ${ex?.response?.data?.message || ex?.message}`
            })
          )
        })
    },
    [patient, dispatch]
  )

  const handleInsertNewFreezeVersion = useCallback(e => {
    const newVersion = e.target.value
    if (!newVersion || !/V\d+\.\d+\.\d+/.test(newVersion)) {
      alert('Invalid version format. Please use the format: V2.0.1')

      return
    }
    setConfirmationModalType({ type: 'patientFreeze', version: e.target.value })
  }, [])

  return (
    <>
      <BaseModal
        open={isOpen}
        handleClose={handleClose}
        className={classes.root}
        contentClassName={classes.dialogContent}
        disableEnforceFocus
        title={t('dialogs.treatmentTracker.title')}
        titleClassName={classes.title}
      >
        <Grid className={classes.mainContent}>
          <Grid container direction="row">
            <input type="radio" name="bulk" onChange={() => setIsBulk(false)} checked={!isBulk} />
            <label>Single</label>
            <input type="radio" name="bulk" onChange={() => setIsBulk(true)} checked={isBulk} />
            <label>Bulk</label>
          </Grid>
          {!isBulk && (
            <Grid container direction="row">
              <input
                type="radio"
                name="trackingView"
                onChange={() => setIsRetentionView(false)}
                checked={!isRetentionView}
              />
              <label>Active Tx</label>
              <input
                type="radio"
                name="trackingView"
                onChange={() => setIsRetentionView(true)}
                checked={isRetentionView}
              />
              <label>Retention</label>
            </Grid>
          )}
          <br />
          {!isBulk && (
            <Grid container direction="row">
              <Grid item style={{ fontWeight: 'bold' }}>
                Patient Name:
              </Grid>
              <Grid item style={{ marginleft: 5 }}>
                {patient?.details?.name}
              </Grid>
              <Copy
                style={{ cursor: 'pointer', marginLeft: 10 }}
                onClick={() => {
                  alert('PatientId copied')
                  navigator.clipboard.writeText(patient?.id)
                }}
              />
            </Grid>
          )}
          <br />
          <Grid container direction="row">
            <Grid item style={{ fontWeight: 'bold' }}>
              Patient Freeze on version:
            </Grid>
            {isEditingFreezeVersion ? (
              <Grid item style={{ marginLeft: 5 }}>
                <input placeholder="format: V2.0.1" onBlur={handleInsertNewFreezeVersion} />
              </Grid>
            ) : (
              <Grid item style={{ marginLeft: 5 }} onClick={() => setIsEditingFreezeVersion(true)}>
                {patientAppSettings.txTrackerVersion?.modelVersionId
                  ? patientAppSettings.txTrackerVersion.modelVersionId
                  : 'None'}
              </Grid>
            )}
            {patientAppSettings.txTrackerVersion?.modelVersionId && (
              <Grid
                item
                style={{ cursor: 'pointer', marginLeft: 10 }}
                onClick={() => setConfirmationModalType({ type: 'patientFreeze' })}
              >
                <TrashIcon />
              </Grid>
            )}
          </Grid>
          <br />
          {isBulk ? (
            <>
              <Grid container direction="row">
                <textarea
                  placeholder="put patientIds. comma separated"
                  style={{ width: '50%' }}
                  onChange={e => setPatientIds(e.target.value)}
                />
              </Grid>
            </>
          ) : isRetentionView ? (
            <>
              <Grid container direction="row">
                <Grid item style={{ fontWeight: 'bold' }}>
                  Retention References:
                </Grid>
              </Grid>
              {retentionReferenceRows === null ? (
                // The one answer that must not render like "fine and empty": support would
                // read that as "never uploaded" and tell the doctor to re-upload into the
                // same corrupt blob.
                <span style={{ color: 'red', fontWeight: 500 }}>
                  Retention stack unreadable: Treatment.txTrackerRetentionStls holds something that is not a reference
                  map. Fix the stored JSON before touching this patient's references.
                </span>
              ) : retentionReferenceRows.length === 0 ? (
                <>No Retention References</>
              ) : (
                retentionReferenceRows.map(row => (
                  <Grid container direction="row" key={row.referenceId} style={{ marginBottom: 5 }}>
                    <Grid item xs={3}>
                      <b>Ref:</b> {row.referenceId?.substring(0, 8)} {row.isActive && <b>(active)</b>}
                    </Grid>
                    <Grid item xs={2}>
                      <b>Provider:</b> {row.provider || 'unknown'}
                    </Grid>
                    <Grid item xs={4}>
                      <b>Uploaded:</b> {row.createdAt ? moment(row.createdAt).format('LLL') : 'unknown'}
                    </Grid>
                    <Grid item xs={2}>
                      <span style={{ fontWeight: 500, color: statusColor(row.status) }}>{row.status || 'unknown'}</span>
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton
                        size="small"
                        aria-label="Copy reference id"
                        onClick={() => {
                          alert('ReferenceId copied')
                          navigator.clipboard.writeText(row.referenceId)
                        }}
                      >
                        <Copy />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))
              )}
              <br />
              <Grid container direction="row">
                <Grid item style={{ fontWeight: 'bold' }}>
                  Retention Scans:
                </Grid>
              </Grid>
              {retentionScanRows.length === 0 ? (
                <>No Retention-Stamped Scans</>
              ) : (
                retentionScanRows.map(({ scan: s, referenceId }) => {
                  const trackerData = parseTxTrackerData(s.treatmentTrackerData)

                  return (
                    <Grid container direction="row" key={s.id} style={{ marginBottom: 5 }} alignItems="center">
                      <Grid item xs={3}>
                        <b>Scan Date:</b> ({moment(s.createdAt).format('LL')})
                      </Grid>
                      <Grid item xs={2}>
                        <b>Ref:</b> {referenceId ? referenceId.substring(0, 8) : 'none (held)'}
                      </Grid>
                      <Grid item xs={3}>
                        <b>Running date:</b>{' '}
                        {trackerData.startedAt ? moment(trackerData.startedAt).format('LLL') : 'never ran'}
                        {trackerData.taskExecutionDetails?.modelVersionId &&
                          ` (${trackerData.taskExecutionDetails.modelVersionId})`}
                      </Grid>
                      <Grid item xs={1}>
                        <span style={{ fontWeight: 500, color: statusColor(s.treatmentTrackerStatus) }}>
                          {s.treatmentTrackerStatus || 'none'}
                        </span>
                      </Grid>
                      <Grid item xs={3}>
                        <IconButton
                          size="small"
                          aria-label="Copy scan id"
                          onClick={() => {
                            alert('ScanId copied')
                            navigator.clipboard.writeText(s.id)
                          }}
                        >
                          <Copy />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Delete scan tx results"
                          style={{ marginLeft: 10 }}
                          onClick={() => {
                            setConfirmationModalType({ type: 'scanResults', scanId: s.id })
                          }}
                        >
                          <TrashIcon />
                        </IconButton>
                        <Button
                          size="small"
                          variant="outlined"
                          style={{ marginLeft: 10 }}
                          onClick={() => setConfirmationModalType({ type: 'retentionRerun', scanId: s.id })}
                        >
                          Rerun
                        </Button>
                      </Grid>
                    </Grid>
                  )
                })
              )}
            </>
          ) : (
            <>
              {parsedTxTrackerStls.stls && (
                <Grid container direction="row" alignItems="center">
                  <Grid item style={{ fontWeight: 'bold' }}>
                    Uploaded STLs:
                  </Grid>
                  <Grid item style={{ marginLeft: 5 }}>
                    <PreviouslyUploadedStls
                      variant="flatten"
                      stls={parsedTxTrackerStls.stls}
                      provider={parsedTxTrackerStls.provider}
                      treatmentType={treatmentType}
                    />
                  </Grid>
                  <Grid
                    item
                    style={{ cursor: 'pointer', marginLeft: 10 }}
                    onClick={() => setConfirmationModalType({ type: 'stls' })}
                  >
                    <TrashIcon />
                  </Grid>
                </Grid>
              )}

              {patientDoctorAppSettings.txTrackerSettings?.force ||
                (patientDoctorAppSettings.txTrackerSettings?.experimental && (
                  <Grid container direction="row">
                    <Grid item style={{ fontWeight: 'bold' }}>
                      Practice Has {patientDoctorAppSettings.txTrackerSettings?.force ? 'force' : 'experimental'} on
                      version:
                    </Grid>
                    <Grid item style={{ marginLeft: 5 }}>
                      {patientDoctorAppSettings.txTrackerSettings?.force ||
                        patientDoctorAppSettings.txTrackerSettings?.experimental}
                    </Grid>
                  </Grid>
                ))}

              <br />
              {activeScanRows.length === 0 ? (
                <>No Scans With Tx Tracker Status</>
              ) : (
                activeScanRows.map(s => (
                    <Grid container direction="row" key={s.createdAt} style={{ marginBottom: 5 }}>
                      <Grid item container>
                        {s.applianceIndex && (
                          <Grid item>
                            <span>Aligner: {s.applianceIndex}</span>
                          </Grid>
                        )}
                        <Grid item xs={4} style={{ paddingLeft: 10 }}>
                          <b>Scan Date:</b> ({moment(s.createdAt).format('LL')})
                        </Grid>
                        <Grid item xs={4}>
                          <b>Running date:</b>{' '}
                          {moment(JSON.parse(s.treatmentTrackerData || '{}').startedAt).format('LLL')}{' '}
                          <span
                            style={{ fontWeight: 'bold', cursor: 'pointer' }}
                            onClick={() =>
                              setConfirmationModalType({
                                type: 'patientFreeze',
                                version: JSON.parse(s.treatmentTrackerData || '{}').taskExecutionDetails?.modelVersionId
                              })
                            }
                          >
                            ({JSON.parse(s.treatmentTrackerData || '{}').taskExecutionDetails?.modelVersionId})
                          </span>
                        </Grid>
                        <Grid item xs={2}>
                          <span style={{ fontWeight: 500, color: statusColor(s.treatmentTrackerStatus) }}>
                            {s.treatmentTrackerStatus}
                          </span>
                        </Grid>
                        <Grid item xs={1}>
                          <Copy
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              alert('ScanId copied')
                              navigator.clipboard.writeText(s.id)
                            }}
                          />
                          <TrashIcon
                            style={{ cursor: 'pointer', marginLeft: 10 }}
                            onClick={() => {
                              setConfirmationModalType({ type: 'scanResults', scanId: s.id })
                            }}
                          />
                        </Grid>
                        {s.withAligner && <Grid item>' - Scan is marked With Aligner'</Grid>}
                      </Grid>
                    </Grid>
                  ))
              )}
            </>
          )}
          <br />
          {/* The bottom actions operate on the active plan (history-wide rerun, plan
              deletion), so they stay off the Retention view: its per-scan actions are the
              only retention-safe operations. */}
          {(isBulk || !isRetentionView) && (
            <div className={classes.buttonsContainer}>
              <PrimaryButton isLoading={isLoading} label="Rerun + delete all" onClick={rerunAndDelete} />
              <SecondaryButton isLoading={isLoading} label="Init/Rerun" onClick={initTxTracker} />
              <SecondaryButton isLoading={isLoading} label="Just delete all" onClick={deleteAll} />
              <SecondaryButton isLoading={isLoading} label="Delete all aligners info" onClick={deleteAllAlignersInfo} />
            </div>
          )}
        </Grid>
      </BaseModal>
      <ConfirmationModal
        isOpen={!!confirmationModalType}
        isLoading={isLoading}
        onClose={() => {
          setConfirmationModalType(null)

          if (confirmationModalType?.type === 'patientFreeze') {
            setIsEditingFreezeVersion(false)
          }
        }}
        onConfirm={
          confirmationModalType?.type === 'patientFreeze'
            ? () => setPatientFreezeVersion(confirmationModalType?.version)
            : confirmationModalType?.type === 'scanResults'
            ? () => deleteScanResults(confirmationModalType?.scanId)
            : confirmationModalType?.type === 'stls'
            ? () => deleteStls()
            : confirmationModalType?.type === 'retentionRerun'
            ? () => rerunRetentionScan(confirmationModalType?.scanId)
            : () => {}
        }
        title={
          confirmationModalType?.type === 'patientFreeze'
            ? `Are you sure you want to set patient freeze version to: ${
                confirmationModalType?.version ? confirmationModalType?.version : 'None'
              }`
            : confirmationModalType?.type === 'scanResults'
            ? `Are you sure you want to delete the scan tx results?`
            : confirmationModalType?.type === 'stls'
            ? 'Are you sure you want to delete all of the Stls files of this patient?'
            : confirmationModalType?.type === 'retentionRerun'
            ? 'Rerun the retention model for this scan? A completed scan must have its results deleted first.'
            : ''
        }
        text={''}
        additionalText={''}
        acceptButtonText={
          confirmationModalType?.type === 'patientFreeze'
            ? 'Contine'
            : confirmationModalType?.type === 'retentionRerun'
            ? 'Rerun'
            : 'Delete'
        }
      />
    </>
  )
}

export default TxTrackerAdminModal
